// ============================================================
// Dashboard sync — POSTar aggregerad bolagsdata till
// dashboard-api-worker var 5:e sekund (debounced) sa
// dashboard.linespotting.com kan visa fardiga summor.
//
// Pipeline:
//   Firestore (receipts + yearDoc) -> subscribe per bolag
//   -> aggregateReceipts() ger MonthlyRow[12]
//   -> summera ar-totaler + rakna kategori-counts
//   -> debounce 5s -> POST /company/seed
//
// Tyst om VITE_DASHBOARD_KEY saknas (en gangs-varning).
// ============================================================
import { COMPANIES } from '../data/companies'
import {
  subscribeReceipts,
  subscribeYear,
  aggregateReceipts,
  type CloudReceipt,
  type CloudYearDoc,
} from './cloudStore'

const SEED_URL = 'https://dashboard-api-worker.ekobrott.workers.dev/company/seed'
const DEBOUNCE_MS = 5000

interface CompanyPayload {
  id: string
  name: string
  skattekonto: number
  skattekontoUpdated: string | null
  vatBalance: number
  vatOut: number
  vatIn: number
  revenue: number
  costs: number
  profitLoss: number
  posts: number
  kundfakturor: number
  levFakturor: number
  utlagg: number
  postsSum: number
}

interface SeedPayload {
  companies: CompanyPayload[]
  updatedAt: string
  year: number
}

interface CompanyState {
  receipts: CloudReceipt[]
  yearDoc: CloudYearDoc | null
}

let missingKeyWarned = false

function buildCompanyPayload(
  id: string,
  name: string,
  state: CompanyState,
): CompanyPayload {
  const receipts = state.receipts
  const months = aggregateReceipts(receipts)

  let vatIn = 0
  let vatOut = 0
  let revenue = 0
  let costs = 0
  for (const m of months) {
    vatIn += m.vatIn
    vatOut += m.vatOut
    revenue += m.income
    costs += m.expense
  }

  let kundfakturor = 0
  let levFakturor = 0
  let utlagg = 0
  let postsSum = 0
  for (const r of receipts) {
    if (r.category === 'kundfakturor') kundfakturor++
    else if (r.category === 'leverantorsfakturor') levFakturor++
    else if (r.category === 'utlagg') utlagg++
    if (r.amount != null) postsSum += r.amount
  }

  return {
    id,
    name,
    skattekonto: Math.round(state.yearDoc?.skattekontoSaldo ?? 0),
    skattekontoUpdated: state.yearDoc?.skattekontoUpdated ?? null,
    vatBalance: Math.round(vatOut - vatIn),
    vatOut: Math.round(vatOut),
    vatIn: Math.round(vatIn),
    revenue: Math.round(revenue),
    costs: Math.round(costs),
    profitLoss: Math.round(revenue - costs),
    posts: receipts.length,
    kundfakturor,
    levFakturor,
    utlagg,
    postsSum: Math.round(postsSum),
  }
}

/**
 * Starta synk till dashboard-api-worker. Returnerar en cleanup-funktion
 * som kopplar bort alla Firestore-subscribers och avbryter pendlande
 * debounce-timer.
 *
 * Om VITE_DASHBOARD_KEY saknas loggas en varning en gang och funktionen
 * returnerar en no-op cleanup (ingen synk sker).
 */
export function startDashboardSync(year: number): () => void {
  const key = import.meta.env.VITE_DASHBOARD_KEY as string | undefined
  if (!key) {
    if (!missingKeyWarned) {
      console.warn(
        '[dashboardSync] VITE_DASHBOARD_KEY saknas — dashboard.linespotting.com kommer inte uppdateras',
      )
      missingKeyWarned = true
    }
    return () => {}
  }

  const states: Record<string, CompanyState> = {}
  for (const c of COMPANIES) {
    states[c.id] = { receipts: [], yearDoc: null }
  }

  let debounceTimer: ReturnType<typeof setTimeout> | null = null
  let stopped = false

  const flush = async () => {
    if (stopped) return
    const payload: SeedPayload = {
      companies: COMPANIES.map((c) =>
        buildCompanyPayload(c.id, c.name, states[c.id]),
      ),
      updatedAt: new Date().toISOString(),
      year,
    }
    try {
      const res = await fetch(SEED_URL, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'X-Dashboard-Key': key,
        },
        body: JSON.stringify(payload),
      })
      if (!res.ok) {
        console.warn(
          '[dashboardSync] POST /company/seed misslyckades (HTTP ' +
            res.status +
            ')',
        )
      }
    } catch (e) {
      console.warn(
        '[dashboardSync] POST /company/seed natverksfel:',
        (e as Error).message,
      )
    }
  }

  const scheduleFlush = () => {
    if (stopped) return
    if (debounceTimer) clearTimeout(debounceTimer)
    debounceTimer = setTimeout(flush, DEBOUNCE_MS)
  }

  const unsubs: Array<() => void> = []
  for (const c of COMPANIES) {
    try {
      unsubs.push(
        subscribeReceipts(c.id, year, (list) => {
          states[c.id].receipts = list
          scheduleFlush()
        }),
      )
      unsubs.push(
        subscribeYear(c.id, year, (doc) => {
          states[c.id].yearDoc = doc
          scheduleFlush()
        }),
      )
    } catch (e) {
      // Inte inloggad eller annat init-fel — tyst skip, ingen synk
      console.warn(
        '[dashboardSync] kunde inte starta subscriber for ' + c.id + ':',
        (e as Error).message,
      )
    }
  }

  return () => {
    stopped = true
    if (debounceTimer) {
      clearTimeout(debounceTimer)
      debounceTimer = null
    }
    for (const u of unsubs) {
      try {
        u()
      } catch {
        // ignore
      }
    }
  }
}
